import { OverallStatusFilter } from './api';

/************************************************************
 *  GENERAL
 ************************************************************/
export type DropdownOption = { value: string; description: string };

/************************************************************
 *  TABLE FILTERS
 ************************************************************/

export type NameObject = {
  first_name: string;
  last_name: string;
};

export type PrescriberNameObject = {
  npi: string;
  name: string;
};

export enum RxFilterType {
  PRESCRIBER = 'prescriber',
  OFFICE = 'office',
  DRUG = 'drug',
  DRUG_CATEGORY = 'drug_category',
  AWAITING_OFFICE = 'awaitingOffice',
  AWAITING_PATIENT = 'awaitingPatient',
  OVERALL_STATUS = 'overallStatus',
}

export type RxFilterValues = {
  [RxFilterType.PRESCRIBER]: string;
  [RxFilterType.OFFICE]: string;
  [RxFilterType.DRUG]: string;
  [RxFilterType.DRUG_CATEGORY]: string;
  [RxFilterType.AWAITING_OFFICE]: boolean;
  [RxFilterType.AWAITING_PATIENT]: boolean;
  [RxFilterType.OVERALL_STATUS]: OverallStatusFilter;
};

export type TableName = 'appeal_reviews' | 'prior_auth' | 'prior_auth_reviews' | 'tasks' | 'rx' | 'notifications';
// the table columns that we currently track in cookies
export type CookieColumnName = 'office_name' | 'prescriber_name';

/************************************************************
 *  SIGNUP FLOW
 ************************************************************/
export type Role =
  | 'administrative_assistant'
  | 'medical_assistant'
  | 'nurse'
  | 'nurse_practitioner'
  | 'office_manager'
  | 'physician'
  | 'physician_assistant'
  | 'practice_manager'
  | 'prior_authorization_specialist'
  | 'other';

export type Credential = 'md' | 'do' | 'pa' | 'np' | 'rn' | 'other' | 'none';

export type HereAs =
  | 'prescriber'
  | 'prescriber_delegate'
  | 'patient'
  | 'pharmaceutical_company'
  | 'insurance_plan'
  | 'pharmacy';

export type HeardAboutFrom =
  | 'my_practice'
  | 'another_practice'
  | 'pharmaceutical_rep'
  | 'pharmacy'
  | 'patient'
  | 'online'
  | 'other';

export type Specialty =
  | 'allergy_and_immunology'
  | 'cardiology'
  | 'dermatology'
  | 'endocrinology'
  | 'gastroenterology'
  | 'obstetrics_and_gynecology'
  | 'hepatology'
  | 'internal_medicine'
  | 'neurology'
  | 'oncology'
  | 'ophthalmology'
  | 'pediatrics'
  | 'psychiatry'
  | 'pulmonology'
  | 'rheumatology'
  | 'urology'
  | 'multi_specialty'
  | 'none'
  | 'other';

export type Size =
  | 'less_than_five'
  | 'five_to_ten'
  | 'eleven_to_thirty'
  | 'thirty_one_to_fifty'
  | 'fifty_one_to_one_hundred'
  | 'one_hundred_plus';

export type Ehr =
  | 'advancedmd'
  | 'athena'
  | 'cerner'
  | 'curemd'
  | 'drchrono'
  | 'eclinicalworks'
  | 'elation'
  | 'ema_modmed'
  | 'epic'
  | 'ezderm'
  | 'ggastro_modmed'
  | 'greenway'
  | 'iclinic_mdland'
  | 'next_gen'
  | 'nextech'
  | 'practice_fusion'
  | 'praxis'
  | 'veradigm'
  | 'other'
  | 'none';

export type ProviderSignupSource = 'signup_flow' | 'onboarding_flow' | 'other';

export interface SignupFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: Role;
  credentials: Credential;
  specify_other_credentials?: string;
  here_as: HereAs;
  heard_about_from: HeardAboutFrom;
  specify_other_heard_about_from: string;
}

export interface MoreInfoFormData {
  practice_name: string;
  specialty?: Specialty;
  specify_other_specialty?: string;
  size_numeric?: number;
  ehr?: Ehr;
  specify_other_ehr?: string;
}

export interface AcceptTermsFormData {
  accepted_terms_at: string;
  tos_s3_key: string;
  tos_version_accepted: string;
  baa_s3_key: string;
  baa_version_accepted: string;
  aa_s3_key: string;
  aa_version_accepted: string;
}

// This is a combination of the above form data interface,
// but these fields are typed as optional because we want to
// allow for partial submissions
export interface AllFormData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: Role;
  credentials?: Credential;
  specify_other_credentials?: string;
  here_as?: HereAs;
  heard_about_from?: HeardAboutFrom;
  specify_other_heard_about_from?: string;
  practice_name?: string;
  specialty?: Specialty;
  specify_other_specialty?: string;
  size_numeric?: number;
  ehr?: Ehr;
  specify_other_ehr?: string;
  accepted_terms_at?: string;
  tos_s3_key?: string;
  tos_version_accepted?: string;
  baa_s3_key?: string;
  baa_version_accepted?: string;
  aa_s3_key?: string;
  aa_version_accepted?: string;
  source?: ProviderSignupSource;
}

/************************************************************
 *  REFERRAL
 ************************************************************/
export interface ReferralFormData {
  // the one doing the referring
  referrer_name: string;
  referrer_email: string;
  // the one being referred
  referred_practice_name: string;
  referred_contact_name: string;
  referred_contact_role: Role | '';
  referred_contact_email: string;
}

/************************************************************
 * ENROLLMENTS
 ************************************************************/
export enum EnrollmentType {
  financial_assistance = 'PAP',
  bridge = 'Bridge',
  hub = 'HUB',
}
